import { AdminApiError, type AdminApi } from '@roomtreatment/api-client';
import type { Scene, JobAction, ModalPipeline, JobPollResponse } from '@roomtreatment/core';
import { createPendingStalenessTracker, STUCK_PENDING_MESSAGE } from './pendingJobStaleness';

const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_POLL_TIMEOUT_MS = 15000;
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 45;

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function defaultNow(): number {
  return Date.now();
}

function defaultNewTraceId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
}

export interface JobRunnerOptions {
  api: AdminApi;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
  maxConsecutiveFailures?: number;
  newTraceId?: () => string;
  onTrace?: (traceId: string, label: string, fields: Record<string, unknown>) => void;
  onQueueInfo?: (info: { position: number; etaSeconds: number } | null) => void;
  sleep?: (ms: number) => Promise<void>;
  /** Injectable clock for deterministic stuck-pending tests. Defaults to Date.now. */
  now?: () => number;
}

export interface JobRunner {
  run<T>(scene: Scene, action: JobAction, pipeline?: ModalPipeline): Promise<T>;
  cancel(): void;
}

export function createJobRunner(opts: JobRunnerOptions): JobRunner {
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const pollTimeoutMs = opts.pollTimeoutMs ?? DEFAULT_POLL_TIMEOUT_MS;
  const maxConsecutiveFailures = opts.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;
  const sleep = opts.sleep ?? defaultSleep;
  const now = opts.now ?? defaultNow;
  const newTraceId = opts.newTraceId ?? defaultNewTraceId;
  const { api, onTrace, onQueueInfo } = opts;

  let cancelled = false;

  return {
    cancel() {
      cancelled = true;
    },

    async run<T>(scene: Scene, action: JobAction, pipeline?: ModalPipeline): Promise<T> {
      cancelled = false;
      const traceId = newTraceId();
      onTrace?.(traceId, 'jobRunner.start', {
        kind: action.kind,
        pipeline: pipeline ?? 'voxel',
      });

      const { job_id } = await api.submitJob(scene, action, pipeline, traceId);
      onTrace?.(traceId, 'jobRunner.submitted', { kind: action.kind, job: job_id });

      let consecutiveFailures = 0;
      const stalenessTracker = createPendingStalenessTracker();
      let nextPollDelayMs = pollIntervalMs;
      for (;;) {
        if (cancelled) throw new Error('Cancelled.');
        await sleep(nextPollDelayMs);
        if (cancelled) throw new Error('Cancelled.');

        let poll: JobPollResponse;
        try {
          poll = await api.pollJob(job_id, traceId, pollTimeoutMs);
        } catch (err) {
          if (err instanceof AdminApiError && err.status === 404) throw err;
          consecutiveFailures += 1;
          onTrace?.(traceId, 'jobRunner.poll_retry', {
            kind: action.kind,
            job: job_id,
            fails: consecutiveFailures,
            error: err instanceof Error ? err.name : 'unknown',
          });
          if (consecutiveFailures >= maxConsecutiveFailures) {
            throw new Error('Lost connection to the solver. Reopen the page to reconnect.');
          }
          nextPollDelayMs = err instanceof AdminApiError && err.retryAfter !== undefined
            ? Math.max(250, err.retryAfter * 1000)
            : pollIntervalMs;
          continue;
        }

        consecutiveFailures = 0;
        nextPollDelayMs = pollIntervalMs;

        if (poll.status === 'pending') {
          if (stalenessTracker.observePending(poll.eta_seconds, poll.resume_count, now())) {
            onTrace?.(traceId, 'jobRunner.stuck_pending', {
              kind: action.kind,
              job: job_id,
              queuePosition: poll.queue_position,
            });
            throw new Error(STUCK_PENDING_MESSAGE);
          }
          onQueueInfo?.(
            typeof poll.queue_position === 'number'
              ? { position: poll.queue_position, etaSeconds: poll.eta_seconds ?? 0 }
              : null,
          );
        } else {
          stalenessTracker.reset();
          onQueueInfo?.(null);
        }

        if (poll.status === 'done') {
          return poll.result as T;
        }
        if (poll.status === 'error') {
          throw new Error(poll.error || 'Job failed.');
        }
      }
    },
  };
}
